import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TopologicalDeviceTelemetryStore } from '@root/app/store/topological-device-telemetry.store';
import { ChartOptions } from 'chart.js';
import {
  ChartComponent,
  XemexChartDataset,
} from '@custom-components/general/chart/chart.component';
import { ChartBuilder } from '../chartBuilder';
import {
  EnergyFlowEmeterIntervalModel,
  EnergyFlowIntervalModel,
} from '@core/models/topological-telemetry.model';
import { MeterRole, PhaseCount } from '@core/models';
import { hasReadings, phaseIndices, readings } from '../phaseUtils';

export type ElectricalMetric = 'voltage' | 'power';

// A zero reading times a negated (production) scale yields JS's -0, which chart
// tooltips/axes render as the string "-0" — normalize it back to +0 (note `-0 === 0`).
function normalizeZero(value: number): number {
  return value === 0 ? 0 : value;
}

// Per-metric config: which emeter array field backs it, its display unit, the factor
// to convert the raw reading (power comes in W, shown in kW), whether a summed "Total"
// line makes sense, whether the axis is pinned to 0, how the line area is filled ('none',
// 'baseline', or 'negative' to shade only the below-zero portion), and an optional dashed
// limit line.
const METRIC_CONFIG: Record<
  ElectricalMetric,
  {
    field: keyof EnergyFlowEmeterIntervalModel;
    unit: string;
    scale: number;
    withTotal: boolean;
    startAtZero: boolean;
    fill: 'none' | 'baseline' | 'negative';
    referenceValue: number | null;
    // Initial y-axis band; the axis still auto-expands when a value falls outside it.
    suggestedMin?: number;
    suggestedMax?: number;
  }
> = {
  voltage: {
    field: 'voltageInV',
    unit: 'V',
    scale: 1,
    withTotal: false,
    startAtZero: false,
    fill: 'none',
    referenceValue: 253,
    suggestedMin: 220,
    suggestedMax: 250,
  },
  power: {
    field: 'activePowerInW',
    unit: 'kW',
    scale: 0.001,
    withTotal: true,
    startAtZero: false,
    fill: 'negative',
    referenceValue: null,
  },
};

// Shared phase → colour map so L1/L2/L3 carry the same colour in every per-phase chart
// (power and voltage), with a distinct near-black for the summed total.
const PHASE_COLORS = ['#2F7ED8', '#C43C8E', '#E0A21E'];
const TOTAL_COLOR = '#111827';

// One emeter that contributes readings: either the grid connection or a labelled channel.
interface MeterSource {
  label: string;
  // True only for the main DLMS grid channel — gates the T6 production sign-flip,
  // which never applies to emeter subchannels.
  isGrid: boolean;
  getEmeter: (interval: EnergyFlowIntervalModel) => EnergyFlowEmeterIntervalModel | undefined;
}

@Component({
  selector: 'xemex-electrical-insights',
  imports: [ChartComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './electrical.component.html',
})
export class ElectricalComponent {
  telemetryStore = inject(TopologicalDeviceTelemetryStore);
  translateService = inject(TranslateService);

  metric = input.required<ElectricalMetric>();
  // RDT-589: when '1', clamp every source to its single active phase instead of the full
  // telemetry-array-derived phase count. Unset/'3'/'unknown' keep current behavior unchanged.
  phaseCountOverride = input<PhaseCount | undefined>();
  meterRole = input<MeterRole>('bidirectional');

  // Unit shown at the top-left of the chart (the axis ticks stay bare numbers).
  unit = computed<string>(() => METRIC_CONFIG[this.metric()].unit);

  // Area fill for the line datasets: off for voltage, and for power only the negative
  // (export) portion is shaded while positive power stays line-only.
  filled = computed<boolean>(() => METRIC_CONFIG[this.metric()].fill !== 'none');
  fillMode = computed<'baseline' | 'negative'>(() =>
    METRIC_CONFIG[this.metric()].fill === 'negative' ? 'negative' : 'baseline',
  );

  chartOptions = computed<ChartOptions>(() => {
    const cfg = METRIC_CONFIG[this.metric()];
    return {
      scales: {
        y: {
          // Off unless the metric pins to zero, so voltage honours its suggested band and
          // signed power can dip below the axis.
          beginAtZero: cfg.startAtZero,
          ...(cfg.startAtZero ? { min: 0 } : {}),
          ...(cfg.suggestedMin != null ? { suggestedMin: cfg.suggestedMin } : {}),
          ...(cfg.suggestedMax != null ? { suggestedMax: cfg.suggestedMax } : {}),
        },
      },
    };
  });

  private readonly _chartData = computed(() => {
    if (this.telemetryStore.isLoadingDeviceTelemetry()) return undefined;

    const cfg = METRIC_CONFIG[this.metric()];
    const builder = new ChartBuilder(this.telemetryStore, this.translateService).withChartSetup();
    const intervals = builder.chartIntervals;
    const labels = builder.chartLabels;

    const sources = this.meterSources(intervals, cfg.field);
    const showMeterName = sources.length > 1;

    const datasets: XemexChartDataset[] = [];
    sources.forEach(source => {
      // Read-time negation (T6): only the main DLMS grid channel's power reading flips
      // for a 'production' role meter — a different mechanism from the kWh selector-level
      // gating in EmeterComponent (T5), and never applied to emeter subchannels.
      const scale =
        this.metric() === 'power' && source.isGrid && this.meterRole() === 'production'
          ? -cfg.scale
          : cfg.scale;
      const indices = phaseIndices(
        intervals,
        source.getEmeter,
        cfg.field,
        this.phaseCountOverride(),
      );
      indices.forEach(phase => {
        datasets.push({
          type: 'line',
          label: this.phaseLabel(source, phase, indices.length, showMeterName),
          data: intervals.map(interval =>
            this.phaseValue(source.getEmeter(interval), cfg.field, phase, scale),
          ),
          color: PHASE_COLORS[phase % PHASE_COLORS.length],
        });
      });
      if (cfg.withTotal && indices.length > 1) {
        datasets.push({
          type: 'line',
          label: this.totalLabel(source, showMeterName),
          data: intervals.map(interval =>
            this.totalValue(source.getEmeter(interval), cfg.field, scale),
          ),
          color: TOTAL_COLOR,
        });
      }
    });

    // Dashed limit line (e.g. the 253 V upper voltage bound), drawn only when there is
    // data to compare against. Empty label + dash keep it out of the legend and unfilled.
    if (cfg.referenceValue != null && datasets.length > 0) {
      datasets.push({
        type: 'line',
        label: '',
        data: intervals.map(() => cfg.referenceValue as number),
        color: '#9CA3AF',
        borderDash: [6, 4],
      });
    }

    return {
      labels,
      datasets: builder.foldRepeatedHour(datasets),
      note: builder.chartNote,
      badge: builder.chartBadge,
    };
  });

  chartLabels = computed<string[]>(() => this._chartData()?.labels ?? []);
  chartDatasets = computed<XemexChartDataset[]>(() => this._chartData()?.datasets ?? []);
  chartNote = computed(() => this._chartData()?.note);
  chartBadge = computed(() => this._chartData()?.badge);

  // Grid connection first, then every labelled emeter channel that carries this metric.
  private meterSources(
    intervals: EnergyFlowIntervalModel[],
    field: keyof EnergyFlowEmeterIntervalModel,
  ): MeterSource[] {
    const sources: MeterSource[] = [];

    if (intervals.some(i => hasReadings(i.electricityGrid, field))) {
      sources.push({
        label: this.translateService.instant('devices.detailTabs.insights.grid.title'),
        isGrid: true,
        getEmeter: i => i.electricityGrid,
      });
    }

    const channels = new Map<number, string>();
    for (const interval of intervals) {
      for (const emeter of interval.emeters ?? []) {
        if (hasReadings(emeter, field) && !channels.has(emeter.channelNumber)) {
          channels.set(emeter.channelNumber, emeter.label ?? `Emeter ${emeter.channelNumber}`);
        }
      }
    }
    for (const [channelNumber, label] of channels) {
      sources.push({
        label,
        isGrid: false,
        getEmeter: i => i.emeters?.find(e => e.channelNumber === channelNumber),
      });
    }

    return sources;
  }

  private phaseValue(
    emeter: EnergyFlowEmeterIntervalModel | undefined,
    field: keyof EnergyFlowEmeterIntervalModel,
    phase: number,
    scale: number,
  ): number | null {
    const value = readings(emeter, field)?.[phase];
    return value == null ? null : normalizeZero(value * scale);
  }

  private totalValue(
    emeter: EnergyFlowEmeterIntervalModel | undefined,
    field: keyof EnergyFlowEmeterIntervalModel,
    scale: number,
  ): number | null {
    const values = readings(emeter, field);
    if (!values) return null;
    const present = values.filter((v): v is number => v != null);
    return present.length ? normalizeZero(present.reduce((sum, v) => sum + v, 0) * scale) : null;
  }

  private phaseLabel(
    source: MeterSource,
    phase: number,
    _phaseCount: number,
    showMeterName: boolean,
  ): string {
    const phasePart = `${this.translateService.instant('devices.detailTabs.insights.phase')} ${phase + 1}`;
    return showMeterName ? `${source.label} ${phasePart}` : phasePart;
  }

  private totalLabel(source: MeterSource, showMeterName: boolean): string {
    const total = this.translateService.instant('devices.detailTabs.insights.total');
    return showMeterName ? `${source.label} ${total}` : total;
  }
}
